/**
 * /campaigns/os-targeting — per-campaign OS targeting page.
 *
 * Declares a TanStack Router route and renders the headline + `OsTargetingForm`.
 * The route auto-registers against the scaffolded `rootRoute` via a module
 * augmentation in the scaffold's `router.tsx` — any future agent / the root
 * wire-up cycle picks it up by importing from this module.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { OsTargetingForm } from "@/features/os-targeting/OsTargetingForm";

// ---------------------------------------------------------------------------
// Route — mounts at `/campaigns/os-targeting`.
// `validateSearch` reserves room for a `?campaignId=` deep-link so operators
// can open the page from a campaign detail view with the id prefilled.
// ---------------------------------------------------------------------------

interface OsTargetingSearch {
	campaignId?: string;
}

export const campaignsOsTargetingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/campaigns/os-targeting",
	validateSearch: (raw: Record<string, unknown>): OsTargetingSearch => {
		const campaignId = raw.campaignId;
		if (typeof campaignId === "string" && campaignId.trim().length > 0) {
			return { campaignId: campaignId.trim() };
		}
		return {};
	},
	component: CampaignsOsTargetingPage,
});

export default campaignsOsTargetingRoute;

// ---------------------------------------------------------------------------
// Page — static shell + form card. No client-side auth wiring yet; the
// global `apiClient` handles the `x-tenant-id` header.
// ---------------------------------------------------------------------------

export function CampaignsOsTargetingPage() {
	const { campaignId } = campaignsOsTargetingRoute.useSearch();

	return (
		<main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<OsTargetingForm initialCampaignId={campaignId} />
			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header fragments — keep purely presentational. No emojis, no competitor
// naming per brand rules.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Campaigns</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">OS targeting</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Targeting
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Operating system targeting
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Constrain a campaign to iOS, Android, Windows, or macOS traffic.
				Rules are enforced at the tracker edge — non-matching clicks never
				reach attribution.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Allow list mode tracks traffic from the selected systems only.
				</li>
				<li>
					Deny list mode blocks the selected systems, tracking everything else.
				</li>
				<li>
					Changes take effect on the next click — there is no deploy step.
				</li>
			</ul>
		</aside>
	);
}
